import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import SelectionPanelComponent from './SelectionPanelComponent';

import { resetChart, removeKPIData, removeSelectedKPI, getChartData } from '../../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/actions';
import {
    reloadKPIsOnTabChange,
    addOffGraphSignal,
    removeOffGraphSignal,
    clearOffGraphSignals,
    loadCapabilityAndKPIs,
    setSelectedAsset
} from '../actions';
import { CHART_MAX_KPI_LIMIT, opButtons } from '../../../PowertrainAssetDetails/CrossAssetAnalysis/ChartView/constants';
import {
    getSelectedAsset,
    getSelectedAssetCapability,
    getSelectedAssetSubscription,
    getKpiLoadingStatus,
    getOffGraphSignals,
    getPowertrainListForBrowser,
    getPowertrainListLoadingStatusForBrowser,
    getSelectedCategory,
    getSelectedPowertrainId
} from '../selectors';
import { allAssetTypes, apiCallStatus, contractInfoMessages } from 'helpers/constants';
import { AnalyticsApi } from 'api';
import { getContractInfo } from '../../../PowertrainAssetDetails/helpers';
import newCapabilityHelper from 'helpers/newCapabilityHelper';
import * as crossAssetAnalysisConstants from '../constants';
import { conditionIndicesViewOptions } from '../../ConditionIndicesOverviewV2/constants';
import { findAncestorNodeIds } from '../../ConditionIndicesOverviewV2/HierarchyView/hierarchyHelpers';
import {
    filterAssetsBySearchAndType,
    getNormalizedAssetId,
    isSameAsset,
    normalizeCrossAssetV2Asset
} from '../helpers/assetIdentity';

const {
    browserCategories,
    scatterHistogramAssistCardTypes
} = crossAssetAnalysisConstants;

const getAssetId = (asset, kpi) => asset?.id ?? asset?.assetId ?? kpi?.assetId;

const getAssetIdFromSignalKey = (signalKey) => {
    if (!signalKey || typeof signalKey !== 'string') { return ''; }
    const firstDelimiter = signalKey.indexOf('|');
    if (firstDelimiter < 0) { return ''; }
    const secondDelimiter = signalKey.indexOf('|', firstDelimiter + 1);
    if (secondDelimiter < 0) { return ''; }
    const parsedAssetId = signalKey.slice(firstDelimiter + 1, secondDelimiter);
    return parsedAssetId ? String(parsedAssetId) : '';
};

const getStrictAssetId = (asset) => {
    if (!asset) { return ''; }
    const rawAssetId = asset?.assetId;
    return rawAssetId === null || rawAssetId === undefined ? '' : String(rawAssetId);
};

const getComparisonCandidateAssetIds = (item, clickedAsset, signalIdentity) => {
    const ids = [
        item?.kpi?.assetId === null || item?.kpi?.assetId === undefined ? '' : String(item.kpi.assetId),
        item?.componentKPIType?.[0]?.assetId === null || item?.componentKPIType?.[0]?.assetId === undefined
            ? ''
            : String(item.componentKPIType[0].assetId),
        getAssetIdFromSignalKey(item?.key),
        getAssetIdFromSignalKey(signalIdentity),
        getStrictAssetId(item?.asset),
        getStrictAssetId(item?.component),
        getStrictAssetId(clickedAsset)
    ].filter(Boolean);

    return Array.from(new Set(ids));
};

const collectHierarchyAssets = (node, assets) => {
    if (!node) { return; }
    if (Array.isArray(node)) {
        node.forEach((child) => collectHierarchyAssets(child, assets));
        return;
    }
    if (typeof node !== 'object') { return; }

    const hasAssetIdentity = node?.assetId !== undefined || node?.id !== undefined || node?.componentId !== undefined;
    const hasAssetShape = Boolean(
        node?.assetName || node?.name || node?.componentName || node?.assetType || node?.componentTypeName || node?.assetTypeName
    );

    if (hasAssetIdentity && hasAssetShape) {
        assets.push(node);
    }

    Object.keys(node).forEach((key) => {
        const value = node[key];
        if (Array.isArray(value) || value && typeof value === 'object') {
            collectHierarchyAssets(value, assets);
        }
    });
};

const getSignalIdentity = (kpi, asset, activeTab) => {
    const assetId = getAssetId(asset, kpi);
    if (!assetId) { return null; }

    let keyByTab;
    if (activeTab === opButtons.histogramV2) {
        keyByTab = kpi?.histogramKey ?? kpi?.measurementTypeIdentifier;
    } else if (activeTab === opButtons.scatterPlotV2) {
        keyByTab = kpi?.scatterKey ?? kpi?.measurementTypeIdentifier;
    } else {
        keyByTab = kpi?.timeseriesKey ?? kpi?.measurementTypeKey ?? kpi?.timeseriesNameTextKey;
    }

    if (!keyByTab) { return null; }
    return `${activeTab}|${assetId}|${keyByTab}`;
};

const toOffGraphSignal = (kpi, asset, activeTab) => {
    const key = getSignalIdentity(kpi, asset, activeTab);
    return key ? { key, kpi, asset } : null;
};

// Finds the KPI in the target asset's kpiList that corresponds to the donor signal.
// Needed because timeseriesKey/histogramKey/scatterKey can be asset-specific.
// Matches only on raw API fields present in kpiList.kpis (displayName/name are added later by KPIListComponent).
const resolveMatchingKpi = (donorKpi, targetKpis, activeTab) => {
    if (!donorKpi || !targetKpis?.length) { return null; }
    if (activeTab === opButtons.histogramV2) {
        return targetKpis.find(k =>
            k.histogramKey && k.histogramKey === donorKpi.histogramKey ||
            k.nameTextKey && k.nameTextKey === donorKpi.nameTextKey
        ) || null;
    }
    if (activeTab === opButtons.scatterPlotV2) {
        return targetKpis.find(k =>
            k.scatterKey && k.scatterKey === donorKpi.scatterKey ||
            k.nameTextKey && k.nameTextKey === donorKpi.nameTextKey
        ) || null;
    }
    // Trend: prefer timeseriesNameTextKey (localization key), fall back to timeseriesName (raw string)
    return targetKpis.find(k =>
        k.timeseriesNameTextKey && k.timeseriesNameTextKey === donorKpi.timeseriesNameTextKey ||
        k.timeseriesName && k.timeseriesName === donorKpi.timeseriesName
    ) || null;
};

// Derive how many signals from the last-configured asset could be copied
const getSameSignalsCount = (chartData, selectedAsset, offGraphSignals, kpiList, activeTab) => {
    if (!chartData?.length || !selectedAsset) { return 0; }

    const selectedId = String(selectedAsset?.id || selectedAsset?.assetId || '');

    // Don't show card if selected asset already has signals in on-graph or off-graph
    const alreadyInOnGraph = (chartData || []).some(item => {
        const itemAsset = item?.asset || item?.component;
        return String(itemAsset?.id || itemAsset?.assetId || '') === selectedId;
    });
    const alreadyInOffGraph = (offGraphSignals || []).some(item => {
        const itemAsset = item?.asset || item?.component;
        return String(itemAsset?.id || itemAsset?.assetId || '') === selectedId;
    });
    if (alreadyInOnGraph || alreadyInOffGraph) { return 0; }

    const lastItem = chartData[chartData.length - 1];
    const lastAsset = lastItem?.asset || lastItem?.component;
    if (!lastAsset) { return 0; }

    const lastId = String(lastAsset?.id || lastAsset?.assetId || '');
    if (!lastId || lastId === selectedId) { return 0; }

    // Handle both flat componentTypeName and nested componentType.typeName
    const lastType = lastAsset?.componentType?.typeName || lastAsset?.componentTypeName || lastAsset?.assetType || '';
    const selectedType = selectedAsset?.componentType?.typeName || selectedAsset?.componentTypeName || selectedAsset?.assetType || '';
    if (!lastType || lastType !== selectedType) { return 0; }

    const donorSignals = chartData.filter(item => {
        const itemAsset = item?.asset || item?.component;
        return String(itemAsset?.id || itemAsset?.assetId || '') === lastId;
    });

    // Only count signals the selected asset actually has — mirrors the resolveMatchingKpi check in handleAddSameSignals
    const availableKpis = kpiList?.kpis || [];
    return donorSignals.filter(item => item?.kpi && resolveMatchingKpi(item.kpi, availableKpis, activeTab)).length;
};

const getMatchingKpiForAsset = async (asset, donorKpi, activeTab) => {
    const assetId = asset?.id ?? asset?.assetId;
    if (!assetId || !donorKpi) { return null; }
    try {
        if (activeTab === opButtons.histogramV2) {
            const data = await AnalyticsApi.getHistogramV2Config(assetId);
            return resolveMatchingKpi(donorKpi, data?.histograms || [], activeTab);
        }
        const data = await AnalyticsApi.getScatterV2Config(assetId);
        return resolveMatchingKpi(donorKpi, data || [], activeTab);
    } catch {
        return null;
    }
};

const resolveHierarchyAsset = (candidateAssetIds, clickedAsset, hierarchyAssetIndexById, sectionData, conditionIndicesView) => {
    const matchedById = candidateAssetIds
        .map((assetId) => hierarchyAssetIndexById.get(assetId))
        .find(Boolean);
    if (matchedById) {
        return normalizeCrossAssetV2Asset(matchedById);
    }
    if (!sectionData || candidateAssetIds.length === 0) { return null; }
    const matchedId = candidateAssetIds.find((assetId) =>
        findAncestorNodeIds(sectionData, normalizeCrossAssetV2Asset({ assetId, id: assetId }), conditionIndicesView)?.length > 0
    );
    if (!matchedId) { return null; }
    return normalizeCrossAssetV2Asset({ ...clickedAsset, assetId: matchedId, id: matchedId });
};

const resolveListViewAsset = (candidateAssetIds, clickedAsset, item, sectionData, conditionIndicesView) => {
    if (!sectionData || candidateAssetIds.length === 0) { return null; }
    const matchedId = candidateAssetIds.find((assetId) =>
        findAncestorNodeIds(sectionData, normalizeCrossAssetV2Asset({ assetId, id: assetId }), conditionIndicesView)?.length > 0
    );
    if (!matchedId) { return null; }
    return normalizeCrossAssetV2Asset({
        ...clickedAsset,
        assetId: matchedId,
        id: matchedId,
        name: clickedAsset?.name || clickedAsset?.assetName || item?.kpi?.assetName
    });
};

export const SelectionPanel = () => {
    const dispatch = useDispatch();
    const selectedAsset = useSelector(getSelectedAsset);
    const selectedAssetCapability = useSelector(getSelectedAssetCapability);
    const subscriptionInfo = useSelector(getSelectedAssetSubscription);
    const kpiListLoadingStatus = useSelector(getKpiLoadingStatus);
    const kpiList = useSelector(({ crossAssetAnalysisV2 }) => crossAssetAnalysisV2?.kpiList);
    const offGraphSignals = useSelector(getOffGraphSignals);
    const chartData = useSelector(({ chartView }) => chartView?.chartData);
    const chartLoadingStatus = useSelector(({ chartView }) => chartView?.chartStatus?.loadingStatus);
    const activeTab = useSelector(({ chartView }) => chartView?.activeTab);
    const selectedKPIType = useSelector(({ chartView }) => chartView?.selectedKPIType);
    const selectedCategory = useSelector(getSelectedCategory);
    const selectedPowertrainId = useSelector(getSelectedPowertrainId);
    const powertrainList = useSelector(getPowertrainListForBrowser);
    const powertrainListLoadingStatus = useSelector(getPowertrainListLoadingStatusForBrowser);
    const assetsData = useSelector(({ conditionIndicesV2 }) => conditionIndicesV2?.assets?.data);
    const hierarchyData = useSelector(({ conditionIndicesV2 }) => conditionIndicesV2?.hierarchy?.data);
    const conditionIndicesView = useSelector(({ conditionIndicesV2 }) => conditionIndicesV2?.conditionIndicesView);
    const powertrainFilter = useSelector(({ conditionIndicesV2 }) => conditionIndicesV2?.filter?.powertrains);
    // Only show off-graph signals that belong to the currently active tab
    const tabOffGraphSignals = useMemo(
        () => (offGraphSignals || []).filter(s => s?.key?.startsWith(`${activeTab}|`)),
        [offGraphSignals, activeTab]
    );

    // Merges contract status into capability data so KPIListComponent can gate signals on it
    const subscriptionInfos = subscriptionInfo?.data?.assetSubscriptionsInfos?.length > 0
        ? subscriptionInfo.data.assetSubscriptionsInfos[0]?.subscriptionInfos
        : [];
    const contractInfo = getContractInfo(
        { details: { assetType: { assetType: selectedAsset?.componentTypeName } }, isMonitored: selectedAsset?.isMonitored ?? true },
        subscriptionInfos,
        subscriptionInfo?.loadingStatus
    );
    const effectiveSelectedAssetCapability = useMemo(() => ({
        ...selectedAssetCapability,
        data: {
            ...selectedAssetCapability?.data,
            contractInfo: { contractInfo }
        }
    }), [selectedAssetCapability, contractInfo]);
    // Also used to hide the "add same signal(s)" suggestion cards for assets without an active subscription
    const hasActiveSubscription = new newCapabilityHelper(effectiveSelectedAssetCapability, selectedAsset).subscriptionStatus() === contractInfoMessages.activeSubscription;

    // Only count if the KPI list is loaded and has entries — avoids showing the button for unsupported asset types
    const sameSignalsCount = hasActiveSubscription && kpiListLoadingStatus === apiCallStatus.SUCCESS && kpiList?.kpis?.length
        ? getSameSignalsCount(chartData, selectedAsset, tabOffGraphSignals, kpiList, activeTab)
        : 0;
    const [selectedComparisonOverrideKey, setSelectedComparisonOverrideKey] = useState(null);
    // Off-graph signal keys being promoted to on-graph — kept visible (greyed out) until data loads
    const [loadingOffGraphSignalKeys, setLoadingOffGraphSignalKeys] = useState(() => new Set());
    // Prevents selectedComparisonAssetId effect from clearing the key set by a comparison item click
    const skipClearOverrideRef = useRef(false);
    const selectedComparisonAssetId = useMemo(() => getNormalizedAssetId(selectedAsset), [selectedAsset]);

    // Refs to capture the previous tab and chartData synchronously in the render body.
    // This is the only reliable way to read chartData before resetChart() clears it
    // on the same render cycle as the tab change.
    const prevActiveTabRef = useRef(activeTab);
    const prevChartDataRef = useRef(chartData);
    const pendingTabSaveRef = useRef(null);

    if (prevActiveTabRef.current !== activeTab) {
        pendingTabSaveRef.current = {
            chartData: prevChartDataRef.current,
            previousTab: prevActiveTabRef.current
        };
    }
    prevActiveTabRef.current = activeTab;
    prevChartDataRef.current = chartData;

    const isScatterOrHistogram = [opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab);

    // The comparison-set item that defines the locked signal — prefers on-graph, but falls back to
    // off-graph so a tab switch (which parks the signal off-graph) still locks the picker to it.
    const lockedSourceItem = useMemo(() => {
        if (!isScatterOrHistogram) { return null; }
        return chartData?.[0] || tabOffGraphSignals?.[0] || null;
    }, [isScatterOrHistogram, chartData, tabOffGraphSignals]);

    // The single signal key that all scatter/histogram comparison items share
    const lockedSignalKey = useMemo(() => {
        if (!lockedSourceItem) { return null; }
        const firstKpi = lockedSourceItem?.kpi;
        return activeTab === opButtons.histogramV2 ? firstKpi?.histogramKey : firstKpi?.scatterKey;
    }, [lockedSourceItem, activeTab]);

    const lockedSignalName = useMemo(() => {
        if (!lockedSignalKey) { return null; }
        return lockedSourceItem?.kpi?.displayName || lockedSourceItem?.kpi?.name;
    }, [lockedSignalKey, lockedSourceItem]);

    const lockedAssetType = useMemo(() => {
        if (!lockedSourceItem) { return null; }
        const firstComponent = lockedSourceItem?.component || lockedSourceItem?.asset;
        return firstComponent?.componentTypeName || firstComponent?.componentType?.typeName;
    }, [lockedSourceItem]);

    // The KPI from the current asset's list that matches the locked signal key
    const matchingLockedKpi = useMemo(() => {
        if (!hasActiveSubscription || !isScatterOrHistogram || !lockedSignalKey || !kpiList?.kpis?.length) { return null; }
        const exactMatch = kpiList.kpis.find(kpi =>
            activeTab === opButtons.histogramV2 ? kpi.histogramKey === lockedSignalKey : kpi.scatterKey === lockedSignalKey
        );
        if (exactMatch) { return exactMatch; }
        // Fallback: match by display name when scatter/histogram keys differ across assets
        if (lockedSignalName) {
            return kpiList.kpis.find(kpi =>
                (kpi.displayName || kpi.name) === lockedSignalName
            ) || null;
        }
        return null;
    }, [hasActiveSubscription, isScatterOrHistogram, lockedSignalKey, lockedSignalName, kpiList, activeTab]);

    // All signals available for the locked-signal dropdown — empty for an unsubscribed asset so its list can't be chosen from
    const scatterHistogramSignals = useMemo(() => {
        if (!hasActiveSubscription || !isScatterOrHistogram) { return []; }
        return (kpiList?.kpis || []).filter(kpi =>
            activeTab === opButtons.histogramV2 ? !!kpi.histogramKey : !!kpi.scatterKey
        );
    }, [hasActiveSubscription, isScatterOrHistogram, kpiList, activeTab]);

    const lockedSignalSelectValue = useMemo(() => {
        if (!matchingLockedKpi) { return lockedSignalKey; }
        return activeTab === opButtons.histogramV2 ? matchingLockedKpi.histogramKey : matchingLockedKpi.scatterKey;
    }, [matchingLockedKpi, lockedSignalKey, activeTab]);

    // Determines which of the 3 assist cards to show
    const scatterHistogramAssistCard = useMemo(() => {
        if (!hasActiveSubscription || !isScatterOrHistogram || !selectedAsset || !lockedSignalKey) { return null; }
        const selectedId = getNormalizedAssetId(selectedAsset);
        const selectedType = selectedAsset?.componentTypeName || selectedAsset?.componentType?.typeName;
        const isInSet = [...chartData || [], ...tabOffGraphSignals].some(item => {
            const itemAsset = item?.asset || item?.component;
            return String(itemAsset?.id || itemAsset?.assetId || '') === selectedId;
        });
        if (selectedType && lockedAssetType && selectedType !== lockedAssetType) {
            return { type: scatterHistogramAssistCardTypes.CANT_JOIN, assetName: selectedAsset?.name || selectedAsset?.componentName, assetType: selectedType, lockedAssetType };
        }
        if (isInSet) {
            return { type: scatterHistogramAssistCardTypes.ALREADY_ADDED, signalName: lockedSignalName };
        }
        if (kpiListLoadingStatus !== apiCallStatus.SUCCESS || !matchingLockedKpi) { return null; }
        return { type: scatterHistogramAssistCardTypes.ADD, signalName: lockedSignalName, lockedKpi: matchingLockedKpi };
    }, [hasActiveSubscription, isScatterOrHistogram, selectedAsset, lockedSignalKey, lockedSignalName, lockedAssetType, chartData, tabOffGraphSignals, matchingLockedKpi]);

    const isListView = conditionIndicesView === conditionIndicesViewOptions.LIST;
    const isPowertrainCategory = isListView && selectedCategory === browserCategories.POWERTRAINS;
    const sectionData = isListView ? assetsData : hierarchyData;

    const hierarchyAssetIndexById = useMemo(() => {
        const assets = [];
        collectHierarchyAssets(hierarchyData, assets);

        const byId = new Map();
        assets.forEach((asset) => {
            const strictAssetId = getStrictAssetId(asset);
            if (strictAssetId && !byId.has(strictAssetId)) {
                byId.set(strictAssetId, asset);
            }
        });

        return byId;
    }, [hierarchyData]);

    const selectedPowertrain = useMemo(() => {
        if (!powertrainList?.length) { return null; }
        return powertrainList.find((item) => {
            const resolvedId = item?.powertrainId ?? item?.systemId ?? item?.id;
            return resolvedId?.toString() === selectedPowertrainId?.toString();
        }) || powertrainList[0];
    }, [powertrainList, selectedPowertrainId]);

    const selectedPowertrainAssets = useMemo(() => selectedPowertrain?.assets || [], [selectedPowertrain]);

    const filteredPowertrainAssets = useMemo(() => {
        const searchText = powertrainFilter?.searchBox || '';
        const typeFilter = powertrainFilter?.assetTypeFilter || allAssetTypes;
        return filterAssetsBySearchAndType(selectedPowertrainAssets, searchText, typeFilter);
    }, [powertrainFilter?.assetTypeFilter, powertrainFilter?.searchBox, selectedPowertrainAssets]);

    const currentPowertrainAssetIdSet = useMemo(() =>
        new Set(filteredPowertrainAssets.map(getNormalizedAssetId).filter(Boolean)),
        [filteredPowertrainAssets]);

    const isAssetInCurrentSection = useCallback((asset) => {
        if (!asset) { return false; }

        if (isPowertrainCategory) {
            const normalizedId = getNormalizedAssetId(asset);
            return normalizedId ? currentPowertrainAssetIdSet.has(normalizedId) : false;
        }

        if (!sectionData) { return false; }
        const normalizedAsset = normalizeCrossAssetV2Asset(asset);
        if (!normalizedAsset?.assetId) { return false; }
        return findAncestorNodeIds(sectionData, normalizedAsset, conditionIndicesView)?.length > 0;
    }, [conditionIndicesView, currentPowertrainAssetIdSet, isPowertrainCategory, sectionData]);

    const selectedSignalKeys = useMemo(() => new Set([
        ...(chartData || []).map((item) => {
            const assetOrComponent = item?.asset || item?.component;
            return getSignalIdentity(item?.kpi, assetOrComponent, activeTab);
        }).filter(Boolean),
        ...tabOffGraphSignals.map((item) => item?.key).filter(Boolean)
    ]), [activeTab, chartData, tabOffGraphSignals]);

    const onGraphSignalKeys = useMemo(() => new Set(
        (chartData || []).map((item) => {
            const assetOrComponent = item?.asset || item?.component;
            return getSignalIdentity(item?.kpi, assetOrComponent, activeTab);
        }).filter(Boolean)
    ), [chartData, activeTab]);

    // Once a promoted signal's data has actually loaded onto the graph, drop it from OFF GRAPH
    useEffect(() => {
        if (!loadingOffGraphSignalKeys.size) { return; }
        const remaining = new Set(loadingOffGraphSignalKeys);
        let changed = false;
        loadingOffGraphSignalKeys.forEach((key) => {
            if (onGraphSignalKeys.has(key)) {
                dispatch(removeOffGraphSignal(key));
                remaining.delete(key);
                changed = true;
            }
        });
        if (changed) { setLoadingOffGraphSignalKeys(remaining); }
    }, [onGraphSignalKeys, loadingOffGraphSignalKeys, dispatch]);

    // If the chart fetch failed, stop showing promoted signals as loading so the user can retry
    useEffect(() => {
        if (chartLoadingStatus === apiCallStatus.ERROR && loadingOffGraphSignalKeys.size) {
            setLoadingOffGraphSignalKeys(new Set());
        }
    }, [chartLoadingStatus, loadingOffGraphSignalKeys]);

    const getComparisonItemSelectionKey = useCallback((item, group, index) => {
        const assetOrComponent = item?.asset || item?.component;
        const identity = getSignalIdentity(item?.kpi, assetOrComponent, activeTab);
        if (identity) {
            return `${group}|${identity}`;
        }
        if (item?.key) {
            return `${group}|${item.key}`;
        }
        const fallbackAssetId = getNormalizedAssetId(item?.asset) || getNormalizedAssetId(item?.component);
        return `${group}|${fallbackAssetId || 'unknown'}|${index}`;
    }, [activeTab]);

    useEffect(() => {
        const activeKeys = new Set([
            ...(chartData || []).map((item, index) => getComparisonItemSelectionKey(item, 'on', index)),
            ...tabOffGraphSignals.map((item, index) => getComparisonItemSelectionKey(item, 'off', index))
        ]);
        if (selectedComparisonOverrideKey && !activeKeys.has(selectedComparisonOverrideKey)) {
            setSelectedComparisonOverrideKey(null);
        }
    }, [chartData, getComparisonItemSelectionKey, offGraphSignals, selectedComparisonOverrideKey]);

    useEffect(() => {
        if (skipClearOverrideRef.current) {
            skipClearOverrideRef.current = false;
            return;
        }
        setSelectedComparisonOverrideKey(null);
    }, [selectedComparisonAssetId]);

    // Reload KPI list when chart tab changes; also move ON GRAPH → OFF GRAPH first
    useEffect(() => {
        const toSave = pendingTabSaveRef.current;
        pendingTabSaveRef.current = null;

        if (toSave?.chartData?.length) {
            toSave.chartData.forEach(item => {
                const assetOrComponent = item?.asset || item?.component;
                if (!item?.kpi || !assetOrComponent) { return; }
                const offSignal = toOffGraphSignal(item.kpi, assetOrComponent, toSave.previousTab);
                if (offSignal) {
                    dispatch(addOffGraphSignal(offSignal));
                }
            });
        }

        setLoadingOffGraphSignalKeys(new Set());
        dispatch(reloadKPIsOnTabChange());
    }, [activeTab, dispatch]);

    const addSignal = useCallback((kpi, asset) => {
        if (!kpi || !asset) { return; }
        // In scatter/histogram mode only the locked signal can be added; match by key OR name (keys differ per-asset)
        if (isScatterOrHistogram && lockedSignalKey) {
            const kpiKey = activeTab === opButtons.histogramV2 ? kpi?.histogramKey : kpi?.scatterKey;
            const kpiName = kpi?.displayName || kpi?.name;
            const isLockedSignal = kpiKey === lockedSignalKey ||
                lockedSignalName && kpiName === lockedSignalName;
            if (!isLockedSignal) { return; }
        }
        const identity = getSignalIdentity(kpi, asset, activeTab);
        if (!identity) { return; }
        if (selectedSignalKeys.has(identity)) { return; }

        if ((chartData || []).length >= CHART_MAX_KPI_LIMIT) {
            const demotedItem = chartData?.[0];
            const demotedAsset = demotedItem?.component || demotedItem?.asset;
            const demotedSignal = toOffGraphSignal(demotedItem?.kpi, demotedAsset, activeTab);
            if (demotedSignal) {
                dispatch(addOffGraphSignal(demotedSignal));
            }
            dispatch(removeKPIData(0));
        }

        dispatch(getChartData(kpi, asset, selectedKPIType, activeTab));
    }, [dispatch, activeTab, selectedKPIType, selectedSignalKeys, chartData]);

    const removeSignal = useCallback((kpi, { isVisibleSelected }) => {
        if (!kpi || !selectedAsset) { return; }

        // In locked scatter/histogram mode clicking the locked-signal row on an asset NOT yet in the
        // set should ADD it (the row shows as selected to keep it enabled, but nothing is in the set)
        if (isScatterOrHistogram && lockedSignalKey && !isVisibleSelected) {
            const kpiKey = activeTab === opButtons.histogramV2 ? kpi?.histogramKey : kpi?.scatterKey;
            if (kpiKey === lockedSignalKey) {
                addSignal(kpi, selectedAsset);
                return;
            }
        }

        if (isVisibleSelected) {
            dispatch(removeSelectedKPI(kpi, selectedAsset, selectedKPIType, activeTab));
            return;
        }

        const identity = getSignalIdentity(kpi, selectedAsset, activeTab);
        if (identity) {
            dispatch(removeOffGraphSignal(identity));
        }
    }, [dispatch, selectedAsset, selectedKPIType, activeTab, isScatterOrHistogram, lockedSignalKey, addSignal]);

    const handleClearBucket = useCallback(() => {
        dispatch(resetChart());
        dispatch(clearOffGraphSignals(activeTab));
        setSelectedComparisonOverrideKey(null);
    }, [dispatch, activeTab]);

    const handleComparisonItemClick = useCallback((item, group, index) => {
        const shouldSyncHierarchySelection = conditionIndicesView === conditionIndicesViewOptions.HIERARCHY;
        const clickedAsset = item?.asset || item?.component;
        const signalIdentity = getSignalIdentity(item?.kpi, item?.asset || item?.component || clickedAsset, activeTab);
        const candidateAssetIds = getComparisonCandidateAssetIds(item, clickedAsset, signalIdentity);
        let normalizedAsset = normalizeCrossAssetV2Asset(clickedAsset);

        if (shouldSyncHierarchySelection && candidateAssetIds.length === 0) {
            return;
        }

        if (shouldSyncHierarchySelection) {
            normalizedAsset = resolveHierarchyAsset(candidateAssetIds, clickedAsset, hierarchyAssetIndexById, sectionData, conditionIndicesView) || normalizedAsset;
        } else {
            const resolvedListAsset = resolveListViewAsset(candidateAssetIds, clickedAsset, item, sectionData, conditionIndicesView);
            if (resolvedListAsset) { normalizedAsset = resolvedListAsset; }
        }

        if (!normalizedAsset && candidateAssetIds.length > 0) {
            const fallbackAssetId = candidateAssetIds[0];
            normalizedAsset = normalizeCrossAssetV2Asset({
                assetId: fallbackAssetId,
                id: fallbackAssetId,
                name: item?.kpi?.assetName
            });
        }

        if (!normalizedAsset) {
            return;
        }

        const isClickableInCurrentSection = isAssetInCurrentSection(normalizedAsset);
        const clickedItemKey = getComparisonItemSelectionKey(item, group, index);
        setSelectedComparisonOverrideKey(clickedItemKey);

        if (shouldSyncHierarchySelection && !isClickableInCurrentSection) {
            return;
        }

        if (isSameAsset(selectedAsset, normalizedAsset)) {
            return;
        }

        skipClearOverrideRef.current = true;
        dispatch(setSelectedAsset(normalizedAsset));
        dispatch(loadCapabilityAndKPIs(normalizedAsset));
    }, [
        conditionIndicesView,
        activeTab,
        dispatch,
        getComparisonItemSelectionKey,
        hierarchyAssetIndexById,
        isAssetInCurrentSection,
        sectionData,
        selectedAsset
    ]);

    const handleAddSameSignals = useCallback(() => {
        if (!chartData?.length || !selectedAsset) { return; }
        // KPI list must be fully loaded for selectedAsset before we can resolve signal keys
        if (kpiListLoadingStatus !== apiCallStatus.SUCCESS) { return; }
        const lastItem = chartData[chartData.length - 1];
        const lastAsset = lastItem?.asset || lastItem?.component;
        if (!lastAsset) { return; }
        const lastId = String(lastAsset?.id || lastAsset?.assetId || '');
        const signalsToAdd = chartData.filter(item => {
            const itemAsset = item?.asset || item?.component;
            return String(itemAsset?.id || itemAsset?.assetId || '') === lastId;
        });
        const availableKpis = kpiList?.kpis || [];
        // track how many on-graph slots we've filled in this loop so partial fills work (e.g. 5/6 + 2)
        let onGraphSlotsUsed = 0;
        signalsToAdd.forEach(item => {
            if (!item?.kpi) { return; }
            // Resolve the KPI belonging to selectedAsset that matches the donor signal.
            // Keys like timeseriesKey can be asset-specific; never reuse them across assets.
            const resolvedKpi = resolveMatchingKpi(item.kpi, availableKpis, activeTab);
            if (!resolvedKpi) { return; }
            const identity = getSignalIdentity(resolvedKpi, selectedAsset, activeTab);
            if (identity && selectedSignalKeys.has(identity)) { return; }
            const currentOnGraphCount = (chartData || []).length + onGraphSlotsUsed;
            if (currentOnGraphCount >= CHART_MAX_KPI_LIMIT) {
                const offSignal = toOffGraphSignal(resolvedKpi, selectedAsset, activeTab);
                if (offSignal) {
                    dispatch(addOffGraphSignal(offSignal));
                }
            } else {
                dispatch(getChartData(resolvedKpi, selectedAsset, selectedKPIType, activeTab));
                onGraphSlotsUsed++;
            }
        });
    }, [chartData, selectedAsset, selectedKPIType, activeTab, dispatch, selectedSignalKeys, kpiList, kpiListLoadingStatus]);

    const handlePromoteOffGraphSignal = useCallback((signal) => {
        if (!signal?.kpi || !signal?.asset || !signal?.key) { return; }
        if (loadingOffGraphSignalKeys.has(signal.key)) { return; } // already loading — ignore repeat clicks

        if ((chartData || []).length >= CHART_MAX_KPI_LIMIT) {
            const demotedItem = chartData?.[0];
            const demotedSignal = toOffGraphSignal(demotedItem?.kpi, demotedItem?.asset, activeTab);
            if (demotedSignal) {
                dispatch(addOffGraphSignal(demotedSignal));
            }
            dispatch(removeKPIData(0));
        }

        // Keep visible (greyed out) in OFF GRAPH until data loads; the effect above then removes it
        setLoadingOffGraphSignalKeys((prev) => new Set(prev).add(signal.key));
        dispatch(getChartData(signal.kpi, signal.asset, selectedKPIType, activeTab));
    }, [dispatch, chartData, activeTab, selectedKPIType, loadingOffGraphSignalKeys]);

    const handleHideOnGraphSignal = useCallback((signal, index) => {
        const assetOrComponent = signal?.asset || signal?.component;
        if (!signal?.kpi || !assetOrComponent || index === undefined) { return; }

        const offGraphSignal = toOffGraphSignal(signal.kpi, assetOrComponent, activeTab);
        if (offGraphSignal) {
            dispatch(addOffGraphSignal(offGraphSignal));
        }
        dispatch(removeKPIData(index));
    }, [dispatch, activeTab]);

    const handleRemoveOffGraphSignal = useCallback((signal) => {
        if (signal?.key) {
            dispatch(removeOffGraphSignal(signal.key));
        }
    }, [dispatch]);

    const isSignalSelected = useCallback((kpi, defaultSelected) => {
        if (defaultSelected) {
            return true;
        }
        const identity = getSignalIdentity(kpi, selectedAsset, activeTab);
        return identity ? selectedSignalKeys.has(identity) : false;
    }, [selectedAsset, activeTab, selectedSignalKeys]);

    const handleAddLockedSignal = useCallback(() => {
        if (!scatterHistogramAssistCard?.lockedKpi || !selectedAsset) { return; }
        addSignal(scatterHistogramAssistCard.lockedKpi, selectedAsset);
    }, [scatterHistogramAssistCard, selectedAsset, addSignal]);

    // Re-adds assets with the newly selected locked signal so the dropdown stays visible.
    const handleChangeLockedSignal = useCallback(async (newSignalKey) => {
        if (!newSignalKey || !selectedAsset) { return; }

        const newKpi = scatterHistogramSignals.find(kpi =>
            activeTab === opButtons.histogramV2 ? kpi.histogramKey === newSignalKey : kpi.scatterKey === newSignalKey
        );
        if (!newKpi) { return; }

        const selectedId = String(selectedAsset?.id || selectedAsset?.assetId || '');
        const allItems = [...chartData || [], ...tabOffGraphSignals];
        const otherAssets = new Map();
        allItems.forEach(item => {
            const asset = item?.component || item?.asset;
            const id = String(asset?.id || asset?.assetId || '');
            if (id && id !== selectedId && !otherAssets.has(id)) { otherAssets.set(id, asset); }
        });

        const matchResults = await Promise.all(
            Array.from(otherAssets.values()).map(async asset => ({
                asset,
                matchingKpi: await getMatchingKpiForAsset(asset, newKpi, activeTab)
            }))
        );
        const assetsToAdd = [
            { asset: selectedAsset, matchingKpi: newKpi },
            ...matchResults.filter(({ matchingKpi }) => matchingKpi)
        ];

        dispatch(resetChart());
        dispatch(clearOffGraphSignals(activeTab));
        assetsToAdd.forEach(({ asset, matchingKpi }) => {
            dispatch(getChartData(matchingKpi, asset, selectedKPIType, activeTab));
        });
    }, [dispatch, scatterHistogramSignals, chartData, tabOffGraphSignals, selectedKPIType, activeTab, selectedAsset]);

    // In list/powertrain view, hide signal picker for assets absent from the loaded powertrain list
    const isSelectedAssetInSection = isAssetInCurrentSection(selectedAsset);
    const assetForSection = isSelectedAssetInSection ? selectedAsset : null;
    const effectiveSelectedAsset = isPowertrainCategory && powertrainListLoadingStatus === apiCallStatus.SUCCESS
        ? assetForSection
        : selectedAsset;

    return (
        <SelectionPanelComponent
            selectedAsset={effectiveSelectedAsset}
            selectedAssetCapability={effectiveSelectedAssetCapability}
            hasActiveSubscription={hasActiveSubscription}
            kpiListLoadingStatus={kpiListLoadingStatus}
            kpiList={kpiList}
            selectedKPIType={selectedKPIType}
            chartData={chartData || []}
            offGraphSignals={tabOffGraphSignals}
            loadingOffGraphSignalKeys={loadingOffGraphSignalKeys}
            activeTab={activeTab}
            onClearBucket={handleClearBucket}
            onAddSameSignals={handleAddSameSignals}
            onSelectSignal={addSignal}
            onDeselectSignal={removeSignal}
            isSignalSelected={isSignalSelected}
            onHideOnGraphSignal={handleHideOnGraphSignal}
            onPromoteOffGraphSignal={handlePromoteOffGraphSignal}
            onRemoveOffGraphSignal={handleRemoveOffGraphSignal}
            onComparisonItemClick={handleComparisonItemClick}
            selectedComparisonAssetId={isSelectedAssetInSection ? selectedComparisonAssetId : null}
            selectedComparisonOverrideKey={isSelectedAssetInSection ? selectedComparisonOverrideKey : null}
            getComparisonItemSelectionKey={getComparisonItemSelectionKey}
            sameSignalsCount={sameSignalsCount}
            lockedSignalKey={lockedSignalKey}
            lockedSignalSelectValue={lockedSignalSelectValue}
            lockedSignalName={lockedSignalName}
            scatterHistogramSignals={scatterHistogramSignals}
            scatterHistogramAssistCard={scatterHistogramAssistCard}
            onAddLockedSignal={handleAddLockedSignal}
            onChangeLockedSignal={handleChangeLockedSignal}
        />
    );
};
